'use client'

import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from '@/components/ui/button'
import Input from '@/components/ui/input'
import Textarea from '@/components/ui/textarea'
import Checkbox from '@/components/ui/checkbox'
import Alert from '@/components/ui/alert'
import { createEvent, updateEvent, joinEvent } from '@/services/events'
import { Database } from '@/types/database-extensions'
import { supabase } from '@/services/supabase'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import '@/styles/datepicker-custom.css'
import { GameSelect } from '@/features/games/game-select'
import { getMeetups, Meetup } from '@/services/meetups'
import { Game } from '@/services/games'
import { BGGGameDetails } from '@/services/bgg-api'
import { MockBGGGame } from '@/data/mock-bgg-games'
import { eventFormSchema, EventFormValues, FormContext } from './schemas'
import { ColorPicker } from '@/components/ui/color-picker'

// Define a GameItem type that matches what GameSelect expects
type GameItem = Game | BGGGameDetails | MockBGGGame

// Type guards to determine game type
function isGame(game: GameItem): game is Game {
  return 'created_at' in game
}

function isBGGGameDetails(game: GameItem): game is BGGGameDetails {
  return 'thumbnail' in game && 'minPlayers' in game
}

function isMockBGGGame(game: GameItem): game is MockBGGGame {
  return 'thumbnail_url' in game && 'min_players' in game
}

// Helper functions to safely access properties
function getGameImageUrl(game: GameItem | null): string {
  if (!game) return '/placeholder-game.png'

  if (isGame(game)) return game.image_url || '/placeholder-game.png'

  if (isBGGGameDetails(game)) {
    const image = game.image || '/placeholder-game.png'
    // Ensure BGG image URLs are complete
    if (image && !image.startsWith('http') && !image.startsWith('/')) {
      return `https://boardgamegeek.com/image/${image}`
    }
    return image
  }

  if (isMockBGGGame(game)) {
    const image = game.image_url || '/placeholder-game.png'
    // Ensure mock BGG image URLs are complete
    if (image && !image.startsWith('http') && !image.startsWith('/')) {
      return `https://boardgamegeek.com/image/${image}`
    }
    return image
  }

  return '/placeholder-game.png'
}

function getGameThumbnailUrl(game: GameItem | null): string {
  if (!game) return '/placeholder-game.png'

  if (isGame(game)) return game.image_url || '/placeholder-game.png' // No thumbnail in Game

  if (isBGGGameDetails(game)) {
    const thumbnail = game.thumbnail || '/placeholder-game.png'
    // Ensure BGG thumbnail URLs are complete
    if (thumbnail && !thumbnail.startsWith('http') && !thumbnail.startsWith('/')) {
      return `https://boardgamegeek.com/image/${thumbnail}`
    }
    return thumbnail
  }

  if (isMockBGGGame(game)) {
    const thumbnail = game.thumbnail_url || '/placeholder-game.png'
    // Ensure mock BGG thumbnail URLs are complete
    if (thumbnail && !thumbnail.startsWith('http') && !thumbnail.startsWith('/')) {
      return `https://boardgamegeek.com/image/${thumbnail}`
    }
    return thumbnail
  }

  return '/placeholder-game.png'
}

function getMinPlayers(game: GameItem | null): number | null {
  if (!game) return null

  if (isGame(game)) return game.min_players
  if (isBGGGameDetails(game)) return game.minPlayers || null
  if (isMockBGGGame(game)) return game.min_players

  return null
}

function getMaxPlayers(game: GameItem | null): number | null {
  if (!game) return null

  if (isGame(game)) return game.max_players
  if (isBGGGameDetails(game)) return game.maxPlayers || null
  if (isMockBGGGame(game)) return game.max_players

  return null
}

function getMinPlaytime(game: GameItem | null): number | null {
  if (!game) return null

  if (isGame(game)) return game.min_playtime
  if (isBGGGameDetails(game)) return game.minPlaytime || null
  if (isMockBGGGame(game)) return game.min_playtime

  return null
}

function getMaxPlaytime(game: GameItem | null): number | null {
  if (!game) return null

  if (isGame(game)) return game.max_playtime
  if (isBGGGameDetails(game)) return game.maxPlaytime || null
  if (isMockBGGGame(game)) return game.max_playtime

  return null
}

// Define event types from Supabase schema
type EventInsert = Database['public']['Tables']['events']['Insert']

// Define a flexible game data structure for the form
interface FormGameData {
  id: string
  name: string
  image?: string | null
  thumbnail?: string | null
  min_players?: number | null
  max_players?: number | null
  min_playtime?: number | null
  max_playtime?: number | null
  description?: string | null
  bgg_id?: string | number | null
  year_published?: number | null
  complexity?: number | null
}

interface EventFormProps {
  initialDate?: Date
  initialEndDate?: Date
  initialValues?: Partial<EventFormValues>
  initialGame?: Game | null
  eventId?: string
  onSuccess: (eventId: string) => void
  onCancel?: () => void
  isEdit?: boolean
  useDatePicker?: boolean
  defaultMeetupId?: string
}

function EventForm({
  initialDate,
  initialEndDate,
  initialValues,
  initialGame,
  eventId,
  onSuccess,
  onCancel,
  isEdit = false,
  useDatePicker = true,
  defaultMeetupId,
}: EventFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [selectedGame, setSelectedGame] = useState<GameItem | null>(initialGame || null)
  const [userId, setUserId] = useState<string | null>(null)
  const [showRecurrenceOptions, setShowRecurrenceOptions] = useState(
    initialValues?.is_recurring || false
  )
  const [startDate, setStartDate] = useState<Date | null>(initialDate || null)
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate || null)
  const [meetups, setMeetups] = useState<Meetup[]>([])
  const [isLoadingMeetups, setIsLoadingMeetups] = useState(false)
  const [eventColor, setEventColor] = useState<string>(initialValues?.color || '#15803d')

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: {
      ...initialValues,
      start_time: initialValues?.start_time || initialDate?.toISOString() || '',
      end_time: initialValues?.end_time || '',
      is_private: initialValues?.is_private || false,
      is_recurring: initialValues?.is_recurring || false,
      skill_level: initialValues?.skill_level || 'all_levels',
      meetup_id: defaultMeetupId,
      im_playing: initialValues?.im_playing !== undefined ? initialValues.im_playing : true,
    },
  })

  // Handle start date change and update end date
  const handleStartDateChange = (date: Date | null) => {
    if (!date) return

    setStartDate(date)
    // Set end date to 2 hours after start date
    const newEndDate = new Date(date.getTime() + 2 * 60 * 60 * 1000)
    setEndDate(newEndDate)
    setValue('start_time', date.toISOString().slice(0, 16))
    setValue('end_time', newEndDate.toISOString().slice(0, 16))
  }

  // Handle end date change
  const handleEndDateChange = (date: Date | null) => {
    if (!date) return

    setEndDate(date)
    setValue('end_time', date.toISOString().slice(0, 16))
  }

  // Watch for form value changes
  const isRecurring = watch('is_recurring')
  const recurrencePattern = watch('recurrence_pattern')

  // Update showRecurrenceOptions when isRecurring changes
  useEffect(() => {
    setShowRecurrenceOptions(isRecurring)
  }, [isRecurring])

  // Handle game selection
  const handleGameSelect = (game: GameItem | null) => {
    setSelectedGame(game)

    if (game) {
      // Get the game ID as a string
      const gameId = game.id.toString()

      // Check if this is a BGG game
      const isBggGame = gameId.startsWith('bgg-')

      // Set the form values appropriately
      if (isBggGame) {
        // For BGG games, store the ID in a separate field
        setValue('bgg_game_id', gameId)
        // Save the game data so we can use it later
        setValue('game_data', JSON.stringify(game))
        // Set game_id to empty to avoid using it
        setValue('game_id', '')
        // Use the game name as the title
        setValue('title', game.name)
      } else {
        // For library games, set the game_id field
        setValue('game_id', gameId)
        // Clear any BGG-specific fields
        setValue('bgg_game_id', '')
        setValue('game_data', '')
        // Use the game name as the title
        setValue('title', game.name)
      }
    } else {
      // Clear all game-related fields if no game selected
      setValue('game_id', '')
      setValue('bgg_game_id', '')
      setValue('game_data', '')
      // Don't clear title when game is deselected to avoid losing user input
    }
  }

  // Use initialGame if provided, or fetch from game_id
  useEffect(() => {
    if (initialGame) {
      setSelectedGame(initialGame)
    } else if (initialValues?.game_id) {
      const fetchGameData = async () => {
        try {
          const { data, error } = await supabase
            .from('games')
            .select('*')
            .eq('id', initialValues.game_id)
            .single()

          if (error) throw error
          if (data) setSelectedGame(data)
        } catch (err) {
          console.error('Error fetching game data:', err)
        }
      }
      fetchGameData()
    }
  }, [initialGame, initialValues?.game_id])

  // Fetch meetups effect
  useEffect(() => {
    async function fetchMeetups() {
      setIsLoadingMeetups(true)
      try {
        const { data, error } = await getMeetups()
        if (error) throw new Error(error)
        if (data) {
          setMeetups(data)

          // If we have a defaultMeetupId and it's in the list, highlight it
          if (defaultMeetupId && data.find(m => m.id === defaultMeetupId)) {
            setValue('meetup_id', defaultMeetupId)
          }
        }
      } catch (err) {
        console.error('Error fetching meetups:', err)
      } finally {
        setIsLoadingMeetups(false)
      }
    }

    fetchMeetups()
  }, [defaultMeetupId, setValue])

  // Add form default values
  useEffect(() => {
    if (selectedGame) {
      // Get the player counts from the game
      const minPlayers = getMinPlayers(selectedGame)
      const maxPlayers = getMaxPlayers(selectedGame)

      // Set min_players if available
      if (minPlayers) {
        setValue('min_players', minPlayers, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }

      // Set max_players if available
      if (maxPlayers) {
        setValue('max_players', maxPlayers, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }
    }
  }, [selectedGame, setValue])

  // Set initial form values
  useEffect(() => {
    if (initialDate) {
      setStartDate(initialDate)
      setValue('start_time', initialDate.toISOString().slice(0, 16))
    }
    if (initialEndDate) {
      setEndDate(initialEndDate)
      setValue('end_time', initialEndDate.toISOString().slice(0, 16))
    } else if (initialDate) {
      // If no end date provided but we have a start date, set end to 2 hours later
      const newEndDate = new Date(initialDate.getTime() + 2 * 60 * 60 * 1000)
      setEndDate(newEndDate)
      setValue('end_time', newEndDate.toISOString().slice(0, 16))
    }
    // Initialize color from initialValues if present
    if (initialValues?.color) {
      setEventColor(initialValues.color)
    }
  }, [initialDate, initialEndDate, initialValues, setValue])

  // Handle form submission
  const onSubmit = async (data: EventFormValues) => {
    if (!userId) {
      setError('You must be logged in to create an event')
      return
    }

    setIsSubmitting(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // Check for BGG IDs in meetup_id field to prevent errors
      if (data.meetup_id && data.meetup_id.startsWith('bgg-')) {
        console.warn('Found BGG ID in meetup_id field, correcting:', data.meetup_id)
        data.meetup_id = ''
      }

      // Ensure all required fields are present for EventInsert
      const rawEventData: EventInsert = {
        title: data.title,
        description: data.description,
        location: data.location,
        address: data.address,
        start_time: data.start_time,
        end_time: data.end_time,
        host_id: userId,
        min_players: data.min_players,
        max_players: data.max_players,
        skill_level: data.skill_level,
        is_private: data.is_private,
        game_id: data.game_id, // This will be undefined for BGG games
        meetup_id: data.meetup_id || null, // Ensure this is null if empty
        is_recurring: data.is_recurring,
        recurrence_pattern: data.is_recurring ? data.recurrence_pattern : undefined,
        recurrence_end_date: data.recurrence_end_date,
        recurrence_count: data.is_recurring ? data.recurrence_count : undefined,
        color: eventColor,
      }

      // If we have a BGG game ID but no game_id, we need to handle it specially
      if (data.bgg_game_id && !data.game_id && data.game_data) {
        // Parse the saved game data
        const gameData = JSON.parse(data.game_data)

        // Ensure image URLs are properly formatted with complete URLs
        let imageUrl = getGameImageUrl({ ...gameData, id: data.bgg_game_id }) || ''
        let thumbnailUrl = getGameThumbnailUrl({ ...gameData, id: data.bgg_game_id }) || ''

        // Ensure URLs are complete
        if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
          imageUrl = `https://boardgamegeek.com/image/${imageUrl}`
        }

        if (thumbnailUrl && !thumbnailUrl.startsWith('http') && !thumbnailUrl.startsWith('/')) {
          thumbnailUrl = `https://boardgamegeek.com/image/${thumbnailUrl}`
        }

        // Extract numeric BGG ID from the string (remove 'bgg-' prefix if present)
        const numericBggId = data.bgg_game_id.startsWith('bgg-')
          ? parseInt(data.bgg_game_id.replace('bgg-', ''), 10)
          : parseInt(data.bgg_game_id, 10)

        // Create a text version for notifications
        const minP = getMinPlayers({ ...gameData, id: data.bgg_game_id }) || 1
        const maxP = getMaxPlayers({ ...gameData, id: data.bgg_game_id }) || minP
        const minPT = getMinPlaytime({ ...gameData, id: data.bgg_game_id }) || 0
        const maxPT = getMaxPlaytime({ ...gameData, id: data.bgg_game_id }) || minPT

        const gameInfoText = `Game: ${
          gameData.name
        }\nPlayers: ${minP}-${maxP}\nPlaytime: ${minPT}-${maxPT} min\nBGG ID: ${numericBggId || 0}`

        // Store game information in the event metadata
        rawEventData.metadata = {
          game: {
            name: gameData.name,
            image_url: imageUrl,
            thumbnail_url: thumbnailUrl,
            min_players: minP,
            max_players: maxP,
            min_playtime: minPT,
            max_playtime: maxPT,
            bgg_id: numericBggId || 0,
          },
        }

        // Also include game information in the description for better searchability
        if (rawEventData.description) {
          rawEventData.description += '\n\n' + gameInfoText
        } else {
          rawEventData.description = gameInfoText
        }

        // Explicitly set game_id to null for BGG games to avoid UUID errors
        rawEventData.game_id = null
      }

      // Sanitize event data to remove empty strings for date fields
      const eventData = Object.entries(rawEventData).reduce((acc, [key, value]) => {
        // Check if it's a date field and the value is an empty string
        if (
          ['start_time', 'end_time', 'recurrence_end_date'].includes(key) &&
          (value === '' || value === null)
        ) {
          return acc // Skip this field
        }

        // Skip empty game_id fields entirely
        if (key === 'game_id' && (value === '' || value === undefined)) {
          return acc // Skip game_id if it's empty or undefined
        }

        // Handle meetup_id to prevent UUID error
        if (key === 'meetup_id') {
          // If it's a BGG ID (starts with 'bgg-') or empty, set to null
          if (!value || (typeof value === 'string' && value.startsWith('bgg-'))) {
            return { ...acc, [key]: null } // Set to null for BGG games
          }
        }

        // Otherwise include the field
        return { ...acc, [key]: value }
      }, {} as EventInsert)

      let result

      if (isEdit && eventId) {
        // Update existing event
        const { data: updatedEvent, error: updateError } = await updateEvent(eventId, eventData)

        if (updateError) {
          throw new Error(updateError)
        }

        result = updatedEvent
      } else {
        // Create new event

        // Debug check for BGG IDs accidentally used as meetup_id
        if (
          eventData.meetup_id &&
          typeof eventData.meetup_id === 'string' &&
          eventData.meetup_id.startsWith('bgg-')
        ) {
          console.error('ERROR: Attempting to use BGG ID as meetup_id:', eventData.meetup_id)
          // Force null for meetup_id if it's a BGG ID
          eventData.meetup_id = null
        }

        const { data: createdEvent, error: createError } = await createEvent(eventData)

        if (createError) {
          console.error('Error creating event:', createError)
          throw new Error(createError)
        }

        result = createdEvent
      }

      // Auto-RSVP to the event if im_playing is checked
      if (result && !isEdit && data.im_playing) {
        try {
          // Join the main event (first event if recurring)
          const eventIdToJoin = result.id || (result.events && result.events[0]?.id)
          if (eventIdToJoin) {
            const { error: joinError } = await joinEvent(eventIdToJoin, userId)
            if (joinError) {
              console.error('Error auto-joining event:', joinError)
              // Don't fail the whole operation if join fails
            }
          }
        } catch (joinErr) {
          console.error('Error in auto-RSVP:', joinErr)
          // Don't fail the whole operation
        }
      }

      if (result && onSuccess) {
        // Check if recurring events were created
        if (data.is_recurring && 'recurringCount' in result && result.recurringCount) {
          setSuccessMessage(`Successfully created ${result.recurringCount} recurring events!`)
        }

        // Trigger a calendar refresh event to ensure the new event appears
        window.dispatchEvent(new Event('calendar:refresh'))

        onSuccess(result.id)
      }
    } catch (err) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} event:`, err)
      setError(
        err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} event`
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Fetch user ID on component mount
  useEffect(() => {
    async function fetchUserId() {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (!user) {
          setError('You must be logged in to create an event')
          return
        }
        setUserId(user.id)
      } catch (err) {
        console.error('Error fetching user:', err)
        setError('Failed to get user information')
      }
    }

    fetchUserId()
  }, [])

  return (
    <div className="space-y-6 bg-background p-6 rounded-xl shadow-lg">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {isEdit ? 'Edit Event' : 'Create a New Game Event'}
          </h2>
          <p className="text-gray-400">
            Fill out the details to {isEdit ? 'update your' : 'schedule your board game'} event
          </p>
        </div>
        <div>
          <ColorPicker
            value={eventColor}
            onChange={setEventColor}
            title="Choose event color"
            buttonClassName="bg-background"
          />
        </div>
      </div>

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Game Selection - Only show when not editing */}
        {!isEdit && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white mb-2">Game</label>
            <GameSelect
              onGameSelect={handleGameSelect}
              selectedGame={selectedGame}
              className="mb-1"
            />
          </div>
        )}

        {/* Event Title - Only show if no game is selected or in edit mode */}
        {(!selectedGame || isEdit) && (
          <div className="space-y-2">
            <label htmlFor="title" className="block text-sm font-medium text-white">
              Event Title <span className="text-red-500">*</span>
            </label>
            <Input
              id="title"
              placeholder="e.g., Friday Night Catan"
              {...register('title')}
              className={errors?.title ? 'border-red-500' : ''}
            />
            {errors?.title && (
              <p className="text-sm text-red-500">{String(errors.title.message)}</p>
            )}
          </div>
        )}

        {/* Event Description */}
        <div className="space-y-2">
          <label htmlFor="description" className="block text-sm font-medium text-white">
            Description
          </label>
          <Textarea
            id="description"
            placeholder="Describe your event..."
            {...register('description')}
            className="min-h-[100px]"
          />
        </div>

        {/* Date and Time Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="start_time" className="block text-sm font-medium text-white">
              Start Date & Time <span className="text-red-500">*</span>
            </label>
            {useDatePicker ? (
              <Controller
                control={control}
                name="start_time"
                render={({ field }) => (
                  <DatePicker
                    selected={startDate}
                    onChange={(date: Date | null) => {
                      if (!date) return
                      field.onChange(date.toISOString().slice(0, 16))
                      handleStartDateChange(date)
                    }}
                    showTimeSelect
                    timeFormat="p"
                    timeIntervals={30}
                    dateFormat="MM/dd/yyyy h:mm aa"
                    className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:ring-primary"
                  />
                )}
              />
            ) : (
              <Input
                id="start_time"
                type="datetime-local"
                {...register('start_time')}
                className={errors?.start_time ? 'border-red-500' : ''}
              />
            )}
            {errors?.start_time && (
              <p className="text-sm text-red-500">{String(errors.start_time.message)}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="end_time" className="block text-sm font-medium text-white">
              End Date & Time <span className="text-red-500">*</span>
            </label>
            {useDatePicker ? (
              <Controller
                control={control}
                name="end_time"
                render={({ field }) => (
                  <DatePicker
                    selected={endDate}
                    onChange={(date: Date | null) => {
                      if (!date) return
                      field.onChange(date.toISOString().slice(0, 16))
                      handleEndDateChange(date)
                    }}
                    showTimeSelect
                    timeFormat="p"
                    timeIntervals={30}
                    dateFormat="MM/dd/yyyy h:mm aa"
                    className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:ring-primary"
                  />
                )}
              />
            ) : (
              <Input
                id="end_time"
                type="datetime-local"
                {...register('end_time')}
                className={errors?.end_time ? 'border-red-500' : ''}
              />
            )}
            {errors?.end_time && (
              <p className="text-sm text-red-500">{String(errors.end_time.message)}</p>
            )}
          </div>
        </div>

        {/* Location Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="location" className="block text-sm font-medium text-white">
              Location Name
            </label>
            <Input id="location" placeholder="e.g., Board Game Cafe" {...register('location')} />
          </div>

          <div className="space-y-2">
            <label htmlFor="address" className="block text-sm font-medium text-white">
              Address
            </label>
            <Input id="address" placeholder="e.g., 123 Main St, City" {...register('address')} />
          </div>
        </div>

        {/* Meetup Selection - Only show if no defaultMeetupId is provided and not editing */}
        {defaultMeetupId ? (
          // If defaultMeetupId is provided, set it as a hidden input
          <input type="hidden" {...register('meetup_id')} value={defaultMeetupId} />
        ) : !isEdit ? (
          <div className="space-y-2">
            <label htmlFor="meetup_id" className="block text-sm font-medium text-white">
              Associate with a Meetup
            </label>
            <select
              id="meetup_id"
              {...register('meetup_id')}
              className="w-full bg-background-secondary rounded-md border-transparent p-2"
            >
              <option value="">-- Select a Meetup (Optional) --</option>
              {isLoadingMeetups ? (
                <option disabled>Loading meetups...</option>
              ) : (
                meetups.map(meetup => (
                  <option key={meetup.id} value={meetup.id}>
                    {meetup.title}
                  </option>
                ))
              )}
            </select>
            <p className="text-sm text-gray-400">
              Associating your event with a meetup makes it appear on the meetup's calendar. Each
              meetup has its own dedicated calendar to organize related events.
            </p>
          </div>
        ) : null}

        {/* Player Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="min_players" className="block text-sm font-medium text-white">
              Minimum Players <span className="text-red-500">*</span>
            </label>
            <Input
              id="min_players"
              type="number"
              min="1"
              {...register('min_players')}
              className={errors?.min_players ? 'border-red-500' : ''}
            />
            {errors?.min_players && (
              <p className="text-sm text-red-500">{String(errors.min_players.message)}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="max_players" className="block text-sm font-medium text-white">
              Maximum Players
            </label>
            <Input
              id="max_players"
              type="number"
              min="1"
              {...register('max_players')}
              className={errors?.max_players ? 'border-red-500' : ''}
            />
            {errors?.max_players && (
              <p className="text-sm text-red-500">{String(errors.max_players.message)}</p>
            )}
          </div>
        </div>

        {/* Skill Level */}
        <div className="space-y-2">
          <label htmlFor="skill_level" className="block text-sm font-medium text-white">
            Skill Level <span className="text-red-500">*</span>
          </label>
          <select
            id="skill_level"
            {...register('skill_level')}
            className={`w-full rounded-md border border-gray-700 bg-background px-3 py-2 text-white ${
              errors?.skill_level ? 'border-red-500' : ''
            }`}
          >
            <option value="all_levels">All Levels Welcome</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          {errors?.skill_level && (
            <p className="text-sm text-red-500">{String(errors.skill_level.message)}</p>
          )}
        </div>

        {/* Privacy Setting */}
        <div className="flex items-center space-x-2">
          <Checkbox id="is_private" {...register('is_private')} />
          <label
            htmlFor="is_private"
            className="text-sm font-medium leading-none text-white cursor-pointer"
          >
            Make this event private (invitation only)
          </label>
        </div>

        {/* I'm Playing Checkbox - Only show when not editing */}
        {!isEdit && (
          <div className="flex items-center space-x-2">
            <Checkbox id="im_playing" {...register('im_playing')} defaultChecked={true} />
            <label
              htmlFor="im_playing"
              className="text-sm font-medium leading-none text-white cursor-pointer"
            >
              I'm playing! (Auto-RSVP to this event)
            </label>
          </div>
        )}

        {/* Recurring Event Options */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox id="is_recurring" {...register('is_recurring')} />
            <label
              htmlFor="is_recurring"
              className="text-sm font-medium leading-none text-white cursor-pointer"
            >
              Make this a recurring event
            </label>
          </div>

          {showRecurrenceOptions && (
            <div className="pl-6 space-y-4 border-l-2 border-gray-700 mt-2">
              <div className="space-y-2">
                <label
                  htmlFor="recurrence_pattern"
                  className="block text-sm font-medium text-white"
                >
                  Recurrence Pattern
                </label>
                <select
                  id="recurrence_pattern"
                  {...register('recurrence_pattern')}
                  className="w-full rounded-md border border-gray-700 bg-background px-3 py-2 text-white"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every Two Weeks</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label
                    htmlFor="recurrence_count"
                    className="block text-sm font-medium text-white"
                  >
                    Number of Occurrences
                  </label>
                  <Input
                    id="recurrence_count"
                    type="number"
                    min="1"
                    placeholder="e.g., 10"
                    {...register('recurrence_count')}
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="recurrence_end_date"
                    className="block text-sm font-medium text-white"
                  >
                    End Date (Optional)
                  </label>
                  <Input
                    id="recurrence_end_date"
                    type="date"
                    {...register('recurrence_end_date')}
                  />
                </div>
              </div>

              {recurrencePattern === 'custom' && (
                <div className="bg-gray-800 p-3 rounded-md">
                  <p className="text-sm text-gray-300">
                    Custom recurrence patterns can be configured after creating the event.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          {onCancel && (
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}

          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting
              ? isEdit
                ? 'Updating...'
                : 'Creating...'
              : isEdit
              ? 'Update Event'
              : 'Create Event'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export { EventForm }
